import * as React from "react";
import { BrandMark } from "@authe/ui-core/primitives";
import { useOptionalTheme } from "@authe/ui-core/theme";
import { defaultTokenColor } from "@authe/fe-commons/theme";
import { DEFAULT_BRAND } from "@/config/branding";

export function useDashboardBrand() {
  const theme = useOptionalTheme();
  return { ...DEFAULT_BRAND, ...theme?.brand };
}

/** One identity renderer for the rail, mobile drawer and compact navigation. */
export function DashboardBrand({ compact = false }: { compact?: boolean }) {
  const brand = useDashboardBrand();
  return <div className={compact ? "flex items-center justify-center py-4 px-2" : "flex items-center gap-2 py-4 px-5"} title={compact ? brand.name : undefined}>
    <BrandMark size="sm" />
    {compact ? <span className="sr-only">{brand.name}</span> : <div className="min-w-0 leading-tight">
      <div className="break-words text-sm font-semibold text-foreground">{brand.name}</div>
      <div className="break-words text-2xs text-muted-foreground">{brand.tagline}</div>
    </div>}
  </div>;
}

/** Browser chrome follows the same brand and mode as the visible shell. */
export function DashboardBrandMetadata() {
  const brand = useDashboardBrand();
  const theme = useOptionalTheme();
  const mode = theme?.resolvedMode ?? "light";
  React.useEffect(() => {
    const entries = [
      { selector: 'link[rel="icon"]', tag: "link", key: "href", value: brand.faviconUrl, attrs: { rel: "icon" } },
      { selector: 'meta[name="theme-color"]', tag: "meta", key: "content", value: defaultTokenColor("background", mode), attrs: { name: "theme-color" } },
    ];
    const cleanup = entries.map(({ selector, tag, key, value, attrs }) => {
      const existing = document.head.querySelector(selector);
      const element = existing ?? document.createElement(tag);
      const previous = element.getAttribute(key);
      const previousType = element.getAttribute("type");
      for (const [name, text] of Object.entries(attrs)) if (text) element.setAttribute(name, text);
      if (tag === "link") element.removeAttribute("type");
      if (value) element.setAttribute(key, value);
      if (!existing) document.head.append(element);
      return () => {
        if (!existing) { element.remove(); return; }
        if (previous === null) element.removeAttribute(key); else element.setAttribute(key, previous);
        if (previousType !== null) element.setAttribute("type", previousType);
      };
    });
    return () => cleanup.forEach(fn => fn());
  }, [brand.faviconUrl, mode]);
  return null;
}
